import { motion } from "framer-motion";
import { ArrowUpRight, BookOpen, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { COACHING_IMAGES, COACHING_LINKS, CREDENTIALS } from "@/data/coaching";
import { fadeUp } from "@/lib/motion";

const BackgroundVideo = () => (
  <>
    <video
      data-testid="about-kelly-video"
      className="absolute inset-0 h-full w-full object-cover"
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
    >
      <source src="/videos/coach.webm" type="video/webm" />
      <source src="/videos/coach.mp4" type="video/mp4" />
    </video>
    <div className="absolute inset-0 bg-midnight/80" />
  </>
);

const CoachHeading = () => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
    <h2 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">
      Meet Your Coach <span className="font-normal text-white/85">- Kelly Robinson</span>
    </h2>
    <a
      data-testid="kelly-linkedin-link"
      href={COACHING_LINKS.kellyLinkedIn}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Kelly Robinson on LinkedIn"
      className="flex h-7 w-7 items-center justify-center bg-white text-navy transition-colors hover:bg-mist"
    >
      <Linkedin className="h-4 w-4" />
    </a>
  </div>
);

const CoachBio = () => (
  <div className="mt-6 max-w-3xl">
    <p className="text-base leading-relaxed text-white/80 sm:text-lg">
      From rebuilding life after being fired, raising a child as a single mom, and scaling a business from the ground up, I've lived the highs, the lows, and everything in between. I didn't just study leadership in a book. I earned it in boardrooms, crisis meetings, and late nights spent figuring it out alone.
    </p>
    <p className="mt-4 text-base leading-relaxed text-white/80 sm:text-lg">
      I know what it takes to lead a team, raise a family, and still find time for yourself. I'm not here to give you fortune-cookie advice. I'm here to give you strategy, clarity, and the truth, even when it's uncomfortable. Because that's what real growth takes.
    </p>
  </div>
);

const Credentials = () => (
  <ul data-testid="credentials-list" className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
    {CREDENTIALS.map((c) => (
      <li key={c} className="border border-white/20 bg-midnight/60 px-4 py-3 text-sm text-white/90">{c}</li>
    ))}
  </ul>
);

export const AboutKelly = () => (
  <section id="about-kelly" data-testid="about-kelly-section" className="relative scroll-mt-24 overflow-hidden bg-midnight py-14 text-white sm:py-20">
    <BackgroundVideo />
    <motion.div {...fadeUp} className="relative mx-auto max-w-6xl px-5 sm:px-8">
      <CoachHeading />
      <p className="mt-4 font-heading text-lg font-semibold sm:text-xl text-white/90">I've been there.</p>
      <CoachBio />
      <Credentials />
    </motion.div>
  </section>
);

export const Book = () => (
  <section id="book" data-testid="book-section" className="scroll-mt-24 bg-white py-14 sm:py-20">
    <motion.div
      {...fadeUp}
      className="mx-auto max-w-6xl px-5 sm:px-8"
    >
      <div className="flex items-center gap-4 sm:gap-6">
        <div>
          <h2 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl text-ink">The Book</h2>
          <p className="mt-4 font-heading text-lg font-semibold sm:text-xl text-navy">Next Level Recruiting</p>
        </div>
        <img src={COACHING_IMAGES.book} alt="Next Level Recruiting book by Kelly Robinson" className="h-24 w-auto shrink-0 rotate-6 sm:h-28" />
      </div>
      <p className="mt-6 text-sm leading-relaxed text-ink/70 sm:text-base">
        Engaging, practical, and refreshingly honest. Packed with lessons and strategies from Kelly's failures and successes, it's the guide for talent acquisition professionals, HR generalists, and aspiring leaders who want to leverage their unique personality and become a person of influence.
      </p>
      <Button asChild data-testid="book-cta" className="btn-primary mt-6 h-12 px-6">
        <a href={COACHING_LINKS.book} target="_blank" rel="noopener noreferrer"><BookOpen className="mr-2 h-4 w-4" /> Get your copy <ArrowUpRight className="ml-1.5 h-4 w-4" /></a>
      </Button>
    </motion.div>
  </section>
);
